import { AfterViewInit, Component, OnDestroy, OnInit, Signal, inject } from "@angular/core";
import { SettingMappingModelService } from "@app/settings-components";
import { TranslateService } from "@ngx-translate/core";
import * as Sentry from "@sentry/angular";
import { Subscription } from "rxjs";
import { span_AppResponsiveTimestamp } from "src/app/app.module";
import { IframeMessagesService } from "src/app/shared/iframe-messages-service/iframe-messages.service";
import { applicationStringFromApplicationType, productStringFromApplicationType } from "src/app/views-interfaces/ApplicationType.enum";
import { DashboardsService, FirmsService, ResponseErrorHandlerService } from "../../shared/";
import { AuthService } from "../../shared/auth/auth.service";
import { BroadcastService } from "../../shared/broadcast-service/broadcast.service";
import { CardViewService } from "../../shared/card-view-service/card-view.service";
import { HelpersService } from "../../shared/helpers-service/helpers.service";
import { IDashboardModel } from "../../views-interfaces/dashboard-model.interface";

import { DragAndDropService } from "@app/drag-and-drop/drag-and-drop.service";

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: "app-view",
    templateUrl: "./view.component.html",
    styleUrl: "./view.component.scss",
    standalone: false,
    host: { class: "wk-view" },
})
export class ViewComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly authService = inject(AuthService);
    private readonly cardViewService = inject(CardViewService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly dragAndDropService = inject(DragAndDropService);
    private readonly firmsService = inject(FirmsService);
    private readonly helpersService = inject(HelpersService);
    private readonly iframeMessageService = inject(IframeMessagesService);
    private readonly responseErrorHandler = inject(ResponseErrorHandlerService);
    private readonly settingMappingModelService = inject(SettingMappingModelService);
    private readonly translateService = inject(TranslateService);

    subscription: Subscription;
    showCardDownload = false;
    lang: string;
    subscriptions: Subscription[] = [];
    showBannerSubscription: Subscription;
    hideBannerSubscription: Subscription;
    bannerIsShown = false;
    isFirstLoad = false;
    protected dragging: Signal<boolean> = this.dragAndDropService.isDragging;
    currentDashboard: IDashboardModel;
    spinnerRequested = false;
    private readonly spinnerRequestedSubscription: Subscription;

    constructor() {
        this.lang = this.helpersService.getLang();
        this.subscription = this.helpersService.cardDownloadEvent$.subscribe((status) => {
            this.showCardDownload = status;

            if (this.showCardDownload === false && this.isFirstLoad) {
                this.isFirstLoad = false;
                console.timeEnd("Application responsive elapsed time");

                Sentry.startSpanManual(
                    {
                        name: "WebApp_Performance-ApplicationResponsive",
                        op: "ApplicationResponsive",
                        startTime: span_AppResponsiveTimestamp,
                    },
                    async (span) => {
                        span?.setAttribute("Site URL", this.iframeMessageService.messagesOrigin);
                        span?.setAttribute("Application", applicationStringFromApplicationType(this.currentDashboard.ApplicationType));
                        span?.setAttribute("Product", productStringFromApplicationType(this.currentDashboard.ApplicationType));
                        span?.setAttribute("Firm name", (await this.firmsService.getFirmById(this.authService.currentUser.firm_id)).Name);
                        span?.setAttribute("Dashboard title", this.currentDashboard.Title);
                        span?.end();
                    },
                );
            }
        });

        this.subscription.add(
            this.helpersService.cardDownloadEvent$.subscribe((status) => {
                this.showCardDownload = status;

                if (this.showCardDownload === false && this.isFirstLoad) {
                    this.isFirstLoad = false;
                    console.timeEnd("Application responsive elapsed time");

                    Sentry.startSpanManual(
                        {
                            name: "WebApp_Performance-ApplicationResponsive",
                            op: "ApplicationResponsive",
                            startTime: span_AppResponsiveTimestamp,
                        },
                        async (span) => {
                            span?.setAttribute("Site URL", this.iframeMessageService.messagesOrigin);
                            span?.setAttribute("Application", applicationStringFromApplicationType(this.currentDashboard.ApplicationType));
                            span?.setAttribute("Product", productStringFromApplicationType(this.currentDashboard.ApplicationType));
                            span?.setAttribute("Firm name", (await this.firmsService.getFirmById(this.authService.currentUser.firm_id)).Name);
                            span?.setAttribute("Dashboard title", this.currentDashboard.Title);
                            span?.end();
                        },
                    );
                }
            }),
        );

        // Subscribe to spinner requests for showing/hiding spinner
        this.spinnerRequestedSubscription = this.helpersService.spinnerRequested$.subscribe((status) => {
            this.spinnerRequested = status;
        });
    }

    ngOnInit() {
        this.spinnerRequested = false;
        if (this.cardViewService.settingsSectionChanged) {
            location.reload();
        }
        this.showBannerSubscription = this.responseErrorHandler.showBannerEvent$.subscribe(() => {
            if (this.authService.loggedIn()) {
                this.bannerIsShown = true;
            }
        });

        this.hideBannerSubscription = this.responseErrorHandler.hideBannerEvent$.subscribe(() => {
            if (this.authService.loggedIn()) {
                this.bannerIsShown = false;
            }
        });
    }

    ngAfterViewInit() {
        this.dashboardsService.getCurrentDashboard().then(async (dashboard) => {
            this.currentDashboard = dashboard;
            this.isFirstLoad = true;
            await this.settingMappingModelService.init();
        });
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.showBannerSubscription?.unsubscribe();
        this.hideBannerSubscription?.unsubscribe();
        this.spinnerRequestedSubscription?.unsubscribe();
    }
}
